// Kill Team Draft — card drafting game module.
// Players receive packs, pick one card, and pass the rest. Repeat until
// all packs are exhausted. Packs pass independently (no waiting for others).

export interface KTDConfig {
  packSize: number;
  totalPacks: number;
}

export const DEFAULT_CONFIG: KTDConfig = { packSize: 15, totalPacks: 3 };

export interface KTDCard {
  id: string;
  name: string;
  letter: string;
  number: number;
}

export interface KTDPlayer {
  id: string;
  name: string;
  playerNumber: number;
}

export interface KTDState {
  config: KTDConfig;
  phase: 'drafting' | 'finished';
  players: KTDPlayer[];
  packNumber: number;
  passingDirection: 'left' | 'right';
  decks: Record<string, KTDCard[]>;      // playerId -> drafted cards
  queues: Record<string, KTDCard[][]>;   // playerId -> [currentPack, ...waitingPacks]
  packsActiveThisRound: number;
  remainingCards: KTDCard[];             // undealt cards for future rounds
  rng: number;
}

export type KTDAction =
  | { type: 'selectCard'; playerId: string; cardId: string }
  | { type: 'reorderDeck'; playerId: string; newOrder: string[] }
  | { type: 'resetDraft'; playerId: string };

// --- Seeded RNG (mulberry32) ---

function rngStep(s: number): [number, number] {
  s = (s + 0x6D2B79F5) | 0;
  let t = Math.imul(s ^ (s >>> 15), 1 | s);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [(t ^ (t >>> 14)) >>> 0, s];
}

// --- Card pool ---

function generatePool(): KTDCard[] {
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const pool: KTDCard[] = [];
  for (const letter of letters) {
    for (let n = 1; n <= 20; n++) {
      pool.push({ id: `${letter}${n}`, name: `${letter}${n}`, letter, number: n });
    }
  }
  return pool; // 200 cards — enough for 4 players × 3 packs × 15
}

function shufflePool(pool: KTDCard[], rng: number): [KTDCard[], number] {
  const p = [...pool];
  for (let i = p.length - 1; i > 0; i--) {
    let r: number;
    [r, rng] = rngStep(rng);
    const j = r % (i + 1);
    [p[i], p[j]] = [p[j], p[i]];
  }
  return [p, rng];
}

function distributePacks(
  shuffled: KTDCard[],
  players: KTDPlayer[],
  packSize: number,
): [Record<string, KTDCard[][]>, KTDCard[]] {
  const queues: Record<string, KTDCard[][]> = {};
  for (let i = 0; i < players.length; i++) {
    queues[players[i].id] = [shuffled.slice(i * packSize, (i + 1) * packSize)];
  }
  return [queues, shuffled.slice(players.length * packSize)];
}

// --- Module exports ---

export function createGame(
  config: KTDConfig,
  players: { id: string; name: string }[],
  seed = Date.now(),
): KTDState {
  const ktdPlayers: KTDPlayer[] = players.map((p, i) => ({
    id: p.id,
    name: p.name,
    playerNumber: i + 1,
  }));

  let rng = seed | 0;
  let shuffled: KTDCard[];
  [shuffled, rng] = shufflePool(generatePool(), rng);

  const decks: Record<string, KTDCard[]> = {};
  for (const p of ktdPlayers) decks[p.id] = [];

  const [queues, remainingCards] = distributePacks(shuffled, ktdPlayers, config.packSize);

  return {
    config,
    phase: 'drafting',
    players: ktdPlayers,
    packNumber: 1,
    passingDirection: 'right',
    decks,
    queues,
    packsActiveThisRound: ktdPlayers.length,
    remainingCards,
    rng,
  };
}

export function applyAction(state: KTDState, action: KTDAction): KTDState {
  switch (action.type) {

    case 'selectCard': {
      const { playerId, cardId } = action;
      const playerQueue = state.queues[playerId];
      if (!playerQueue || playerQueue.length === 0) return state;

      const currentPack = playerQueue[0];
      const card = currentPack.find(c => c.id === cardId);
      if (!card) return state;

      const remainingPack = currentPack.filter(c => c.id !== cardId);

      // Immutable queues: shallow-copy outer object, rebuild target entries
      const newQueues: Record<string, KTDCard[][]> = { ...state.queues };
      newQueues[playerId] = playerQueue.slice(1); // remove current pack

      const newDecks = {
        ...state.decks,
        [playerId]: [...(state.decks[playerId] ?? []), card],
      };

      let { packsActiveThisRound, packNumber, passingDirection, remainingCards } = state;
      let phase = state.phase as 'drafting' | 'finished';

      if (remainingPack.length > 0) {
        // Pass remaining pack to the next player
        const idx = state.players.findIndex(p => p.id === playerId);
        const nextIdx = passingDirection === 'right'
          ? (idx + 1) % state.players.length
          : (idx - 1 + state.players.length) % state.players.length;
        const nextId = state.players[nextIdx].id;
        newQueues[nextId] = [...(newQueues[nextId] ?? []), remainingPack];
      } else {
        // Pack exhausted — check if round is over
        packsActiveThisRound -= 1;
        if (packsActiveThisRound === 0) {
          packNumber += 1;
          if (packNumber > state.config.totalPacks) {
            phase = 'finished';
          } else {
            // New round: toggle direction, deal fresh packs
            passingDirection = passingDirection === 'right' ? 'left' : 'right';
            const { packSize } = state.config;
            const newCards = remainingCards.slice(0, state.players.length * packSize);
            remainingCards = remainingCards.slice(state.players.length * packSize);
            const [freshQueues] = distributePacks(newCards, state.players, packSize);
            for (const p of state.players) {
              newQueues[p.id] = freshQueues[p.id];
            }
            packsActiveThisRound = state.players.length;
          }
        }
      }

      return {
        ...state,
        decks: newDecks,
        queues: newQueues,
        packsActiveThisRound,
        packNumber,
        passingDirection,
        phase,
        remainingCards,
      };
    }

    case 'reorderDeck': {
      const { playerId, newOrder } = action;
      const deck = state.decks[playerId] ?? [];
      const reordered = newOrder
        .map(id => deck.find(c => c.id === id))
        .filter((c): c is KTDCard => c !== undefined);
      if (reordered.length !== deck.length) return state;
      return { ...state, decks: { ...state.decks, [playerId]: reordered } };
    }

    case 'resetDraft': {
      let rng = state.rng;
      let shuffled: KTDCard[];
      [shuffled, rng] = shufflePool(generatePool(), rng);

      const decks: Record<string, KTDCard[]> = {};
      for (const p of state.players) decks[p.id] = [];

      const [queues, remainingCards] = distributePacks(shuffled, state.players, state.config.packSize);

      return {
        ...state,
        phase: 'drafting',
        packNumber: 1,
        passingDirection: 'right',
        decks,
        queues,
        packsActiveThisRound: state.players.length,
        remainingCards,
        rng,
      };
    }

    default:
      return state;
  }
}

export function getPlayerView(state: KTDState, _playerId: string | null): KTDState {
  // Relay broadcasts full state to all clients. The frontend shows only each
  // player's own pack — pack contents aren't filtered server-side here.
  return state;
}

export function getValidActions(state: KTDState, playerId: string): KTDAction[] {
  if (state.phase !== 'drafting') return [];
  const pack = state.queues[playerId]?.[0] ?? [];
  return pack.map(c => ({ type: 'selectCard' as const, playerId, cardId: c.id }));
}

export function getBotAction(state: KTDState, playerId: string): KTDAction | null {
  const pack = state.queues[playerId]?.[0] ?? [];
  if (pack.length === 0) return null;
  return { type: 'selectCard', playerId, cardId: pack[0].id };
}

export function describe(config: KTDConfig): string {
  return [
    `Kill Team Draft — ${config.totalPacks} packs of ${config.packSize} cards each. Build your kill team through pick-and-pass drafting.`,
    '',
    'Draft rules:',
    '  Each player opens a pack and picks one card, then passes the remainder to the next player.',
    '  Pack 1 passes right; pack 2 passes left; pack 3 passes right (direction alternates each pack).',
    '  Packs are independent — you can pick as soon as your current pack arrives, no waiting.',
    '  Draft ends when all cards in all packs are picked.',
    '',
    `Each round distributes one pack per player simultaneously from a shared shuffled pool (${config.packSize} cards/pack).`,
    'After drafting, review and reorder your deck before the game begins.',
    '',
    'Note: card content is currently placeholder (letters A–J, numbers 1–20). Real kill team cards are coming.',
  ].join('\n');
}
